import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import DashboardView from "@/components/dashboard/dashboard-view";
import { FullscreenButton } from "@/components/ui/fullscreen-button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { useCompanyContext } from "@/hooks/useCompanyContext";
import { DEFAULT_DASHBOARD_CONFIG } from "@/convex/system/dashboard";
import { RouteGuard } from "@/components/permissions/route-guard";
import type { WidgetType, WidgetSize } from "@/types/dashboard";

const SKELETON_SIZE_CLASS: Record<WidgetSize, string> = {
  sm: "col-span-1",
  md: "col-span-1 md:col-span-2",
  lg: "col-span-1 md:col-span-2 lg:col-span-3",
  xl: "col-span-1 md:col-span-2 lg:col-span-4",
};

function StatSkeleton() {
  return (
    <Card className="py-0 gap-0">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Skeleton className="h-7 w-14 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card className="py-0 gap-0">
      <CardContent className="pt-3 pb-3 px-4">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-6 w-20 rounded" />
        </div>
        <Skeleton className="h-52 w-full rounded" />
      </CardContent>
    </Card>
  );
}

function RecentListSkeleton() {
  return (
    <Card className="py-0 gap-0">
      <CardContent className="pt-3 pb-3 px-4">
        <Skeleton className="h-3 w-32 mb-3" />
        <div className="space-y-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-between p-2.5 rounded-md bg-muted/40"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12 flex-shrink-0" />
                </div>
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function WidgetSkeleton({
  type,
  size,
}: {
  type: WidgetType;
  size: WidgetSize;
}) {
  return (
    <div className={SKELETON_SIZE_CLASS[size]}>
      {type === "stat_card" ? (
        <StatSkeleton />
      ) : type === "recent_list" ? (
        <RecentListSkeleton />
      ) : (
        <ChartSkeleton />
      )}
    </div>
  );
}

const DEFAULT_WIDGETS_SORTED = [...DEFAULT_DASHBOARD_CONFIG.widgets].sort(
  (a, b) => a.order - b.order,
);

function DashboardLoading() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
        {DEFAULT_WIDGETS_SORTED.map((w) => (
          <WidgetSkeleton
            key={w.id}
            type={w.type as WidgetType}
            size={w.size as WidgetSize}
          />
        ))}
      </div>
    </div>
  );
}

function DashboardPage() {
  const {
    hasCompany,
    userContext,
    isLoading: contextLoading,
  } = useCompanyContext();

  const dashboardConfig = useQuery(
    api.system.dashboard.queries.getDashboardConfig,
  );

  const shouldLoadStats =
    hasCompany ||
    (userContext &&
      typeof userContext === "object" &&
      !("error" in userContext) &&
      userContext.companyId);

  if (!shouldLoadStats) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <p className="text-muted-foreground">
          No tienes una empresa asignada. Como super administrador, puedes
          acceder a todas las funcionalidades del sistema.
        </p>
      </div>
    );
  }

  return (
    <RouteGuard module="dashboard" action="read">
      {contextLoading || dashboardConfig === undefined ? (
        <DashboardLoading />
      ) : (
        <>
          <DashboardView />
          <FullscreenButton />
        </>
      )}
    </RouteGuard>
  );
}

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});
